import { useState, useCallback, useRef } from 'react';
import purchaseOrderService from '../services/purchaseOrderService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

const usePurchaseAdvice = () => {
    const [purchaseAdvices, setPurchaseAdvices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const { showUpdate } = useMessageAlert();

    // Deduplication & in-flight tracking
    const lastSignatureRef = useRef('');
    const lastFetchTimeRef = useRef(0);
    const inFlightRef = useRef(new Set());

    const generateSignature = useCallback((params) => JSON.stringify(params || {}), []);

    const shouldDedupe = useCallback((signature, force) => {
        if (force) return false;
        const now = Date.now();
        const timeDiff = now - lastFetchTimeRef.current;
        return signature === lastSignatureRef.current && timeDiff < 200;
    }, []);

    const fetchPurchaseAdvices = useCallback(async (params = {}, options = {}) => {
        const { force = false } = options;
        const signature = generateSignature(params);

        if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
        if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

        setLoading(true);
        setError(null);
        inFlightRef.current.add(signature);

        try {
            const result = await purchaseOrderService.getAllPurchaseAdvices(params);
            if (result.success) {
                const data = result.data;
                // Sort by updated_at or created_at (newest first)
                const sortByDate = (arr) => {
                    return [...arr].sort((a, b) => {
                        const dateA = new Date(a.updated_at || a.created_at || 0);
                        const dateB = new Date(b.updated_at || b.created_at || 0);
                        return dateB - dateA; // Descending order (newest first)
                    });
                };
                if (Array.isArray(data)) {
                    setPurchaseAdvices(sortByDate(data));
                    setTotalPages(1);
                    setTotalCount(data.length);
                } else if (Array.isArray(data.items)) {
                    setPurchaseAdvices(sortByDate(data.items));
                    const total = data.total || data.items.length;
                    setTotalPages(data.totalPages || Math.ceil(total / (params.limit || 20)));
                    setTotalCount(total);
                } else if (Array.isArray(data.data)) {
                    setPurchaseAdvices(sortByDate(data.data));
                    const total = data.total || data.data.length;
                    setTotalPages(data.totalPages || Math.ceil(total / (params.limit || 20)));
                    setTotalCount(total);
                } else {
                    setPurchaseAdvices([]);
                    setTotalPages(0);
                    setTotalCount(0);
                }
                lastSignatureRef.current = signature;
                lastFetchTimeRef.current = Date.now();
            } else {
                setError(result.error);
                setPurchaseAdvices([]);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            setPurchaseAdvices([]);
            return { success: false, error: errorMessage };
        } finally {
            inFlightRef.current.delete(signature);
            setLoading(false);
        }
    }, [generateSignature, shouldDedupe]);

    const getPurchaseAdviceById = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.getPurchaseAdviceById(id);
            if (!result.success) setError(result.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePurchaseAdvice = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.updatePurchaseAdvice(id, payload);
            if (result.success) {
                setPurchaseAdvices(prev => prev.map(item => (String(item.id) === String(id) ? { ...item, ...result.data } : item)));
                showUpdate(i18n.t('purchaseAdvice.notifications.updated', { id: String(id) }));
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [showUpdate]);

    const deletePurchaseAdvice = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.deletePurchaseAdvice(id);
            if (result.success) {
                setPurchaseAdvices(prev => prev.filter(item => String(item.id) !== String(id)));
                setTotalCount(prev => Math.max(0, prev - 1));
                showUpdate(`Purchase Advice ${id} deleted`);
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [showUpdate]);

    const generatePurchaseAdvice = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.generatePurchaseAdvice(id, payload);
            if (!result.success) setError(result.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const startUnloadingPurchaseAdvice = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.startUnloadingPurchaseAdvice(id, payload);
            if (result.success) {
                setPurchaseAdvices(prev => prev.map(item => (String(item.id) === String(id) ? { ...item, ...result.data } : item)));
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const completeUnloadingPurchaseAdvice = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.completeUnloadingPurchaseAdvice(id, payload);
            if (result.success) {
                setPurchaseAdvices(prev => prev.map(item => (String(item.id) === String(id) ? { ...item, ...result.data } : item)));
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const completePurchaseOrder = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.completePurchaseOrder(id);
            if (!result.success) setError(result.error);
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const generateChallanPurchaseAdvice = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.generateAdviceChallan(id, payload);
            if (result.success) {
                setPurchaseAdvices(prev => prev.map(item => (String(item.id) === String(id) ? { ...item, ...result.data } : item)));
                showUpdate(`Challan generated for Purchase Advice ${id}`);
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [showUpdate]);

    const markChallanPrintedPurchaseAdvice = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.markAdviceChallanPrinted(id);
            if (result.success) {
                setPurchaseAdvices(prev => prev.map(item => (String(item.id) === String(id) ? { ...item, ...result.data } : item)));
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, []);

    const markReceivedPurchaseAdvice = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.markAdviceReceived(id);
            if (result.success) {
                setPurchaseAdvices(prev => prev.map(item => (String(item.id) === String(id) ? { ...item, ...result.data } : item)));
                showUpdate(`Purchase Advice ${id} marked as received`);
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [showUpdate]);

    const editPurchaseChallan = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await purchaseOrderService.editPurchaseChallan(id, payload);
            if (result.success) {
                setPurchaseAdvices(prev => prev.map(item => (String(item.id) === String(id) ? { ...item, ...result.data } : item)));
                showUpdate(`Challan updated for Purchase Advice ${id}`);
            } else {
                setError(result.error);
            }
            return result;
        } catch (err) {
            const errorMessage = err.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [showUpdate]);

    const clearError = useCallback(() => setError(null), []);

    return {
        purchaseAdvices,
        loading,
        error,
        totalPages,
        totalCount,
        fetchPurchaseAdvices,
        getPurchaseAdviceById,
        updatePurchaseAdvice,
        deletePurchaseAdvice,
        generatePurchaseAdvice,
        startUnloadingPurchaseAdvice,
        completeUnloadingPurchaseAdvice,
        completePurchaseOrder,
        generateChallanPurchaseAdvice,
        markReceivedPurchaseAdvice,
        markChallanPrintedPurchaseAdvice,
        editPurchaseChallan,
        clearError,
    };
};

export default usePurchaseAdvice;
